import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/hooks/queryKeys";
import { useUiStore } from "@/stores/ui.store";
import { Typography } from "@/components/ui/Typography";
import { formatNextDueDate } from "@/lib/dateUtils";

interface UnitDetail {
  id: string;
  unitId: string;
  alias: string;
  unitNumber: string;
  unitType: string;
  maxTenants: number;
  rentAmount: number;
  dueDate: number;
  securityDeposit: number | null;
  agreementEndDate: string | null;
  noticePeriodDays: number;
  notes: string | null;
  tenants: {
    id: string;
    name: string;
    phone: string;
    email: string | null;
    agreementEndDate: string | null;
    moveInDate: string | null;
  }[];
}

const UNIT_TYPE_LABELS: Record<string, string> = {
  STUDIO: "Studio",
  ONE_BHK: "1 BHK",
  TWO_BHK: "2 BHK",
  THREE_BHK: "3 BHK",
  FOUR_BHK: "4 BHK",
  OTHER: "Other",
};

export function UnitDetailPage() {
  const params = useParams();
  const propertyId = params.id as string;
  const unitId = params.unitId as string;
  const [propertyName, setPropertyName] = useState("");
  const [unit, setUnit] = useState<UnitDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const [propRes, unitRes] = await Promise.all([
          api.get<{ name: string }>(`/properties/${propertyId}`),
          api.get<UnitDetail>(`/units/${unitId}`),
        ]);
        setPropertyName(propRes.name);
        setUnit(unitRes);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load unit");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [propertyId, unitId, refresh]);

  const now = new Date();
  const year = now.getFullYear();
  const from = `${year}-01-01T00:00:00.000Z`;
  const to = `${year}-12-31T23:59:59.999Z`;
  const queryClient = useQueryClient();
  const { data: unitExpenses = [] } = useQuery({
    queryKey: ["expenses", propertyId, unitId, from, to],
    queryFn: () =>
      api.get<{ expenses: { id: string; category: string; amount: number; date: string; paidBy: string }[] }>(
        `/expenses?propertyId=${propertyId}&unitId=${unitId}&from=${from}&to=${to}`
      ).then((r) => r.expenses),
    enabled: !!propertyId && !!unitId,
  });

  const { data: pendingTenants = [], refetch: refetchPendingTenants, isFetching: pendingTenantsLoading } = useQuery({
    queryKey: ["tenant-invites", "pending", unitId],
    queryFn: () =>
      api.get<{ pendingTenants: { id: string; name: string; phone: string; email: string | null; submittedAt: string }[] }>(
        `/tenant-invites/pending?roomId=${unitId}`
      ).then((r) => r.pendingTenants),
    enabled: !!unitId && !!unit,
  });

  const createInviteMutation = useMutation({
    mutationFn: (body: { agreementEndDate?: string; moveInDate?: string }) =>
      api.post<{ link: string; token: string; expiresAt: string }>("/tenant-invites", {
        roomId: unitId,
        ...body,
      }),
  });

  const approveMutation = useMutation({
    mutationFn: (pendingId: string) => api.post(`/tenant-invites/pending/${pendingId}/approve`, {}),
    onSuccess: () => {
      setRefresh((r) => r + 1);
      queryClient.invalidateQueries({ queryKey: queryKeys.unitDetail(unitId) });
      queryClient.invalidateQueries({ queryKey: ["tenant-invites", "pending", unitId] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (pendingId: string) => api.post(`/tenant-invites/pending/${pendingId}/reject`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-invites", "pending", unitId] });
    },
  });

  const addToast = useUiStore((s) => s.addToast);
  const approveAllMutation = useMutation({
    mutationFn: () =>
      api.post<{ approved: number }>("/tenant-invites/pending/approve-all", { roomId: unitId }),
    onSuccess: (data) => {
      setRefresh((r) => r + 1);
      queryClient.invalidateQueries({ queryKey: queryKeys.unitDetail(unitId) });
      queryClient.invalidateQueries({ queryKey: ["tenant-invites", "pending", unitId] });
      addToast({
        type: "success",
        message: data.approved === 1 ? "1 tenant approved" : `${data.approved} tenants approved`,
      });
    },
  });

  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteAgreementEndDate, setInviteAgreementEndDate] = useState("");
  const [inviteMoveInDate, setInviteMoveInDate] = useState("");

  const handleOpenInviteModal = () => {
    setShowInviteModal(true);
    setInviteLink(null);
  };
  const handleGenerateInviteLink = async () => {
    const body: { agreementEndDate?: string; moveInDate?: string } = {};
    if (inviteAgreementEndDate) body.agreementEndDate = inviteAgreementEndDate;
    if (inviteMoveInDate) body.moveInDate = inviteMoveInDate;
    const res = await createInviteMutation.mutateAsync(Object.keys(body).length ? body : {});
    const fullLink = `${typeof window !== "undefined" ? window.location.origin : ""}${res.link}`;
    setInviteLink(fullLink);
    setShowInviteModal(false);
  };
  const handleCopyLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareLink = async () => {
    if (!inviteLink) return;
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "Tenant signup link",
          text: "Use this link to sign up as a tenant.",
          url: inviteLink,
        });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          await navigator.clipboard.writeText(inviteLink);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }
      }
    } else {
      await handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="h-48 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div className="space-y-6">
        <Link
          to="/properties"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to properties
        </Link>
        <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
          <p className="text-red-700 dark:text-red-300 font-medium">{error ?? "Unit not found"}</p>
          <Link to={`/property/${propertyId}`} className="mt-4 inline-block text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
            Back to property
          </Link>
        </div>
      </div>
    );
  }

  const unitTypeLabel = UNIT_TYPE_LABELS[unit.unitType] ?? unit.unitType.replace("_", " ");
  const canAddTenant = unit.tenants.length < unit.maxTenants;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/property/${propertyId}`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 mb-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to {propertyName}
        </Link>
        <Typography variant="h1">{unit.alias} ({unit.unitNumber})</Typography>
        <p className="mt-1 text-slate-500 dark:text-slate-400">{propertyName} · {unitTypeLabel}</p>
      </div>

      {/* Unit details */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/95 p-5 sm:p-6 space-y-4">
        <Typography variant="h2">Unit details</Typography>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">Type</dt>
            <dd className="text-slate-900 dark:text-slate-100 font-medium">{unitTypeLabel}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">Monthly rent</dt>
            <dd className="text-slate-900 dark:text-slate-100 font-semibold">₹{unit.rentAmount.toLocaleString("en-IN")}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">Rent due date</dt>
            <dd className="text-slate-900 dark:text-slate-100">{unit.dueDate}th of each month</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">Next rent due</dt>
            <dd className="text-slate-900 dark:text-slate-100">{formatNextDueDate(unit.dueDate)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">Tenants</dt>
            <dd className="text-slate-900 dark:text-slate-100">{unit.tenants.length} / {unit.maxTenants} max</dd>
          </div>
          {unit.securityDeposit != null && (
            <div>
              <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">Security deposit</dt>
              <dd className="text-slate-900 dark:text-slate-100">₹{unit.securityDeposit.toLocaleString("en-IN")}</dd>
            </div>
          )}
          {unit.agreementEndDate && (
            <div>
              <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">Agreement ends</dt>
              <dd className="text-slate-900 dark:text-slate-100">{new Date(unit.agreementEndDate).toLocaleDateString("en-IN")}</dd>
            </div>
          )}
        </dl>
        {unit.notes && (
          <p className="text-sm text-slate-600 dark:text-slate-300 pt-2 border-t border-slate-100 dark:border-slate-700">{unit.notes}</p>
        )}
      </div>

      {/* Invite tenant link */}
      {canAddTenant && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/95 p-5 sm:p-6">
          <Typography variant="h2" className="mb-2">Invite tenant(s)</Typography>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Share a signup link. Tenants can add up to {unit.maxTenants} person(s) in one go. Set agreement and move-in dates (optional); you can edit each tenant later.
          </p>
          {!inviteLink ? (
            <button
              type="button"
              onClick={handleOpenInviteModal}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600"
            >
              Get tenant signup link
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                readOnly
                value={inviteLink}
                className="flex-1 min-w-0 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
              <button
                type="button"
                onClick={handleCopyLink}
                className="rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 p-2.5 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600"
                title={copied ? "Copied!" : "Copy link"}
                aria-label={copied ? "Copied!" : "Copy link"}
              >
                {copied ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75h6A2.25 2.25 0 0 1 15.75 6v6a2.25 2.25 0 0 1-2.25 2.25h-6A2.25 2.25 0 0 1 3.75 12V6A2.25 2.25 0 0 1 6 3.75Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 9.75h3.75a2.25 2.25 0 0 1 2.25 2.25v6a2.25 2.25 0 0 1-2.25 2.25H16.5" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={handleShareLink}
                className="rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 p-2.5 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600"
                title="Share"
                aria-label="Share link"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                </svg>
              </button>
            </div>
          )}

          {showInviteModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/60" onClick={() => setShowInviteModal(false)}>
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                <Typography variant="h3">Set dates for this invite (optional)</Typography>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  These will apply to all tenants who sign up via this link. You can change them per tenant after approval.
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Agreement end date</label>
                  <input
                    type="date"
                    value={inviteAgreementEndDate}
                    onChange={(e) => setInviteAgreementEndDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Move-in date</label>
                  <input
                    type="date"
                    value={inviteMoveInDate}
                    onChange={(e) => setInviteMoveInDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 rounded-lg border border-slate-200 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleGenerateInviteLink}
                    disabled={createInviteMutation.isPending}
                    className="flex-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600"
                  >
                    {createInviteMutation.isPending ? "Generating…" : "Generate link"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tenants */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/95 p-5 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <Typography variant="h2">Tenants</Typography>
          {canAddTenant ? (
            <Link
              to={`/property/${propertyId}/tenants/new?unitId=${unit.id}`}
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              + Add tenant
            </Link>
          ) : (
            <span className="text-sm text-slate-500 dark:text-slate-400">Max tenants reached</span>
          )}
        </div>
        {unit.tenants.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-4">No tenants in this unit yet.</p>
        ) : (
          <ul className="space-y-4">
            {unit.tenants.map((t) => (
              <li
                key={t.id}
                className="rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-700/30 p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{t.name}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                      <a href={`tel:${t.phone}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                        {t.phone}
                      </a>
                    </p>
                    {t.email && (
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        <a href={`mailto:${t.email}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                          {t.email}
                        </a>
                      </p>
                    )}
                    {(t.agreementEndDate || t.moveInDate) && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                        {t.moveInDate && <>Move-in: {new Date(t.moveInDate).toLocaleDateString("en-IN")}</>}
                        {t.moveInDate && t.agreementEndDate && " · "}
                        {t.agreementEndDate && <>Agreement ends: {new Date(t.agreementEndDate).toLocaleDateString("en-IN")}</>}
                      </p>
                    )}
                  </div>
                  <Link
                    to={`/property/${propertyId}/unit/${unit.id}/tenant/${t.id}/edit`}
                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 shrink-0"
                  >
                    Edit
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pending signups */}
      {(pendingTenants.length > 0 || canAddTenant) && (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div>
              <Typography variant="h2">Pending signups</Typography>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Approve or reject these tenant signups. Approved tenants will appear in the list above.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => refetchPendingTenants()}
                disabled={pendingTenantsLoading}
                className="rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 inline-flex items-center gap-1.5"
                title="Refresh list"
              >
                <svg className={`h-4 w-4 ${pendingTenantsLoading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                Refresh
              </button>
              {pendingTenants.length > 0 && (
                <button
                  type="button"
                  onClick={() => approveAllMutation.mutate()}
                  disabled={approveAllMutation.isPending}
                  className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 shrink-0"
                >
                  {approveAllMutation.isPending ? "Approving…" : "Approve all"}
                </button>
              )}
            </div>
          </div>
          <ul className="space-y-4">
            {pendingTenants.map((p) => (
              <li
                key={p.id}
                className="rounded-xl border border-amber-100 dark:border-amber-800 bg-white dark:bg-slate-800/80 p-4 flex flex-wrap items-center justify-between gap-3"
              >
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{p.name}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <a href={`tel:${p.phone}`} className="text-primary-600 dark:text-primary-400 hover:underline">{p.phone}</a>
                  </p>
                  {p.email && (
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      <a href={`mailto:${p.email}`} className="text-primary-600 dark:text-primary-400 hover:underline">{p.email}</a>
                    </p>
                  )}
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Submitted {new Date(p.submittedAt).toLocaleDateString("en-IN")}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => approveMutation.mutate(p.id)}
                    disabled={approveMutation.isPending}
                    className="rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => rejectMutation.mutate(p.id)}
                    disabled={rejectMutation.isPending}
                    className="rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Unit expenses */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/95 p-5 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <Typography variant="h2">Expenses (this unit)</Typography>
          <Link
            to={`/property/${propertyId}/expenses/new?unitId=${unit.id}`}
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            + Add expense
          </Link>
        </div>
        {unitExpenses.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-4">No unit-specific expenses this year.</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {unitExpenses.map((exp) => (
              <li key={exp.id} className="flex items-center justify-between gap-4 py-3 first:pt-0">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{exp.category.replace("_", " ")}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(exp.date).toLocaleDateString("en-IN")} · {exp.paidBy}</p>
                </div>
                <p className="font-semibold text-slate-900 dark:text-slate-100">₹{exp.amount.toLocaleString("en-IN")}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
